import { ChevronRight } from 'lucide-react'
import type { BrowsePath, BrowseLevel } from '@/stores/useBrowseStore'

interface BrowseBreadcrumbProps {
  path: BrowsePath
  currentLevel: BrowseLevel
  onNavigate: (level: BrowseLevel) => void
}

export function BrowseBreadcrumb({
  path,
  currentLevel,
  onNavigate,
}: BrowseBreadcrumbProps) {
  const segments: { label: string; level: BrowseLevel; isCurrent: boolean }[] =
    []

  segments.push({
    label: 'All Divisions',
    level: 'divisions',
    isCurrent: currentLevel === 'divisions',
  })

  if (path.division) {
    segments.push({
      label: path.division.name,
      level: 'departments',
      isCurrent: currentLevel === 'departments',
    })
  }

  if (path.department) {
    segments.push({
      label: path.department.name,
      level: 'calendars',
      isCurrent: currentLevel === 'calendars',
    })
  }

  if (path.calendar) {
    // Non-seasonal / no-calendar → this segment leads directly to plans
    const skipEvents = !path.calendar.isSeasonal || path.calendar.noCalendar
    segments.push({
      label: path.calendar.calendarName,
      level: skipEvents ? 'plans' : 'events',
      isCurrent: skipEvents
        ? currentLevel === 'plans'
        : currentLevel === 'events',
    })
  }

  if (path.event) {
    segments.push({
      label: path.event.eventName,
      level: 'plans',
      isCurrent: currentLevel === 'plans',
    })
  }

  return (
    <nav
      aria-label="Plan folders"
      className="flex items-center gap-1 text-sm text-muted-foreground"
    >
      {segments.map((segment, index) => (
        <div
          key={segment.level + segment.label}
          className="flex items-center gap-1"
        >
          {index > 0 && <ChevronRight className="h-4 w-4 shrink-0" />}
          {segment.isCurrent ? (
            <span className="font-medium text-foreground">{segment.label}</span>
          ) : (
            <button
              type="button"
              onClick={() => onNavigate(segment.level)}
              className="cursor-pointer hover:text-foreground hover:underline"
            >
              {segment.label}
            </button>
          )}
        </div>
      ))}
    </nav>
  )
}
